/**
 * FOZZIL Pay SDK
 * Lightweight SDK to manage payment methods directly via main ecosystem Supabase
 * User payment methods are stored in the same database as profiles, subscriptions, etc.
 */

import { supabase } from '../supabaseClient';

const FPAY_API_URL = process.env.REACT_APP_FPAY_API_URL || 'https://fpaybeshadesecure.fozzilindustries.workers.dev';

class FozzilPaySDK {
    constructor() {
        this.isReady = false;
        this.callbacks = {};
        this.theme = this._detectTheme();
        
        // PAYMENTS KILL SWITCH — set to true when payments are ready to launch
        this.paymentsEnabled = false;

        // Listen for theme changes
        if (typeof window !== 'undefined' && window.matchMedia) {
            window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
                this.theme = e.matches ? 'dark' : 'light';
            });
        }
    }

    _detectTheme() {
        if (typeof window === 'undefined') return 'light';
        return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }

    /**
     * Open FOZZIL Pay modal via custom event
     * When payments are disabled, shows "Coming Soon" instead
     */
    open(options) {
        if (!this.paymentsEnabled) {
            // Dispatch event to show "Payments Coming Soon" modal
            window.dispatchEvent(new CustomEvent('fozzilpay:coming-soon'));
            return this;
        }

        const { onSuccess, onCancel, onError, ...rest } = options;
        this.callbacks = { onSuccess, onCancel, onError };

        const event = new CustomEvent('fozzilpay:open', {
            detail: { ...rest, theme: this.theme }
        });

        window.dispatchEvent(event);
        return this;
    }

    close() {
        window.dispatchEvent(new CustomEvent('fozzilpay:close'));
        return this;
    }

    handleSuccess(data) { this.callbacks.onSuccess?.(data); }
    handleCancel() { this.callbacks.onCancel?.(); }
    handleError(error) { this.callbacks.onError?.(error); }

    // ============ Supabase-based API Methods ============

    /**
     * Save a payment method to user's account (in main ecosystem Supabase)
     * Checks for duplicates before saving
     */
    async savePaymentMethod(data) {
        try {
            const { user_id, payment_type, display_name, payment_token, provider, last_four, is_primary, metadata } = data;

            if (!user_id || !payment_type || !display_name) {
                return { success: false, message: "Missing required fields" };
            }

            // Check for duplicate payment methods
            const { data: existingMethods } = await supabase
                .from("user_payment_methods")
                .select("*")
                .eq("user_id", user_id)
                .eq("payment_type", payment_type);

            // Payment method limits: 3 UPI, 3 Cards, 1 Net Banking, 1 Wallet
            const limits = { 'UPI': 3, 'Card': 3, 'Net Banking': 1, 'Wallet': 1 };
            const limit = limits[payment_type] || 3;

            if (existingMethods && existingMethods.length >= limit) {
                return { success: false, message: `You can only save up to ${limit} ${payment_type} method${limit > 1 ? 's' : ''}` };
            }

            // Check for duplicates based on payment type
            if (existingMethods && existingMethods.length > 0) {
                for (const method of existingMethods) {
                    // For UPI: check VPA in metadata
                    if (payment_type === 'UPI' && metadata?.vpa) {
                        if (method.metadata?.vpa === metadata.vpa) {
                            return { success: false, message: "This UPI ID is already saved" };
                        }
                    }
                    // For Card: check last 4 digits
                    if (payment_type === 'Card' && last_four) {
                        if (method.last_four === last_four) {
                            return { success: false, message: "A card ending in " + last_four + " is already saved" };
                        }
                    }
                    // For Net Banking: check bank name
                    if (payment_type === 'Net Banking' && metadata?.bank) {
                        if (method.metadata?.bank === metadata.bank) {
                            return { success: false, message: "This bank is already saved for Net Banking" };
                        }
                    }
                    // For Wallet: check provider
                    if (payment_type === 'Wallet' && metadata?.provider) {
                        if (method.metadata?.provider === metadata.provider) {
                            return { success: false, message: "This wallet is already saved" };
                        }
                    }
                }
            }

            // If setting as primary, unset other primary methods first
            if (is_primary) {
                await supabase
                    .from("user_payment_methods")
                    .update({ is_primary: false })
                    .eq("user_id", user_id);
            }

            const { data: savedMethod, error } = await supabase
                .from("user_payment_methods")
                .insert([{
                    user_id,
                    payment_type,
                    display_name,
                    payment_token: payment_token || null,
                    provider: provider || null,
                    last_four: last_four || null,
                    is_primary: is_primary || false,
                    metadata: metadata || {}
                }])
                .select()
                .single();

            if (error) {
                console.error("Save payment method error:", error);
                return { success: false, message: error.message };
            }

            return { success: true, data: savedMethod };
        } catch (error) {
            console.error("Save method error:", error);
            return { success: false, message: "Error saving payment method" };
        }
    }

    /**
     * Get user's saved payment methods
     */
    async getPaymentMethods(user_id) {
        try {
            const { data, error } = await supabase
                .from("user_payment_methods")
                .select("*")
                .eq("user_id", user_id)
                .order("is_primary", { ascending: false })
                .order("created_at", { ascending: false });

            if (error) {
                console.error("Fetch payment methods error:", error);
                return { success: false, message: error.message, data: [] };
            }

            return { success: true, data: data || [] };
        } catch (error) {
            console.error("Error fetching payment methods:", error);
            return { success: false, message: "Error fetching payment methods", data: [] };
        }
    }

    /**
     * Delete a payment method
     */
    async deletePaymentMethod(id) {
        try {
            const { error } = await supabase
                .from("user_payment_methods")
                .delete()
                .eq("id", id);

            if (error) {
                return { success: false, message: error.message };
            }

            return { success: true, message: "Payment method deleted" };
        } catch (error) {
            return { success: false, message: "Error deleting payment method" };
        }
    }

    /**
     * Set a payment method as primary
     */
    async setPrimaryPaymentMethod(id, user_id) {
        try {
            // Unset all other primary methods
            await supabase
                .from("user_payment_methods")
                .update({ is_primary: false })
                .eq("user_id", user_id);

            // Set this one as primary
            const { data, error } = await supabase
                .from("user_payment_methods")
                .update({ is_primary: true })
                .eq("id", id)
                .select()
                .single();

            if (error) {
                return { success: false, message: error.message };
            }

            return { success: true, data };
        } catch (error) {
            return { success: false, message: "Error setting primary method" };
        }
    }

    /**
     * Validate a UPI VPA (via FPay backend API)
     */
    async validateVPA(vpa) {
        try {
            const response = await fetch(`${FPAY_API_URL}/api/payments/validate-vpa`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ vpa })
            });
            return await response.json();
        } catch (error) {
            console.error('Error validating VPA:', error);
            // Return success anyway for now since VPA validation is optional
            return { is_valid: true, status: 'success' };
        }
    }

    // ============ Address Management ============

    /**
     * Get user's saved addresses (Secondary addresses)
     */
    async getAddresses(user_id) {
        try {
            // Mocking to prevent 404 while table is missing
            /* const { data, error } = await supabase
                .from("user_addresses")
                .select("*")
                .eq("user_id", user_id)
                .order("created_at", { ascending: false });

            if (error) {
                console.error("Fetch addresses error:", error);
                return { success: false, message: error.message, data: [] };
            }

            return { success: true, data: data || [] }; */
            return { success: true, data: [] };
        } catch (error) {
            console.error("Error fetching addresses:", error);
            return { success: false, message: "Error fetching addresses", data: [] };
        }
    }

    /**
     * Save a new address
     */
    async saveAddress(data) {
        try {
            const { user_id, first_name, last_name, address_line, city, state, pincode, country_region } = data;

            // Basic validation
            if (!user_id || !address_line || !city || !state || !pincode) {
                return { success: false, message: "Missing required address fields" };
            }

            // Check address limit (max 4 secondary addresses + 1 profile primary = 5 total)
            const { data: existingAddresses } = await supabase
                .from("user_addresses")
                .select("id")
                .eq("user_id", user_id);

            if (existingAddresses && existingAddresses.length >= 4) {
                return { success: false, message: "You can only save up to 5 addresses (including primary)" };
            }

            const { data: savedAddress, error } = await supabase
                .from("user_addresses")
                .insert([{
                    user_id,
                    first_name,
                    last_name,
                    address_line,
                    city,
                    state,
                    pincode,
                    country_region: country_region || 'India'
                }])
                .select()
                .single();

            if (error) {
                return { success: false, message: error.message };
            }

            return { success: true, data: savedAddress };
        } catch (error) {
            return { success: false, message: "Error saving address" };
        }
    }

    /**
     * Delete an address
     */
    async deleteAddress(id) {
        try {
            const { error } = await supabase
                .from("user_addresses")
                .delete()
                .eq("id", id);

            if (error) {
                return { success: false, message: error.message };
            }

            return { success: true, message: "Address deleted" };
        } catch (error) {
            return { success: false, message: "Error deleting address" };
        }
    }
}

// Create singleton instance
const FozzilPay = new FozzilPaySDK();

// Expose to window for non-React usage
if (typeof window !== 'undefined') {
    window.FozzilPay = FozzilPay;
}

export default FozzilPay;
export { FozzilPaySDK };
